import React from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router'
import {
    Card,
    CardContent,
    CardActions,
    Box,
    Avatar,
    Typography,
    styled,
    Link,
} from '@mui/material'
import appConfig from 'config'
import { TextFormatter, Button } from 'components'
import { MIDPathToImage } from 'packages/core/helpers'
import { useTranslation } from 'react-i18next'
import { Tooltip } from 'packages/eid-ui'

const colors = {
    primary: '#0057A0',
    grey: '#b4b4b4',
    border: '#f2f2f3',
}

const MyCard = styled(Card)({
    margin: 'auto',
    padding: '0px 10px 5px 10px',
})

const StyledCardActions = styled(CardActions)({
    paddingBottom: '12px',
    marginTop: '10px',
    '& button': {
        '&:hover': {
            boxShadow: '0 5px 5px -2px rgba(48, 127, 193, 0.55)',
        },
    },
})

const StyledCardContent = styled(CardContent)({
    padding: '10px 15px',
    borderBottom: '1px solid ' + colors.border,
    display: 'flex',
})

const StyledTypography = styled(Typography)({
    color: '#0057A0',
    fontSize: '14px',
    textAlign: 'left',
    textTransform: 'capitalize',
})

const StyledFieldBlock = styled(Box)({
    '& div': {
        textAlign: 'left',
    },
    '&:first-of-type': {
        paddingRight: '10px',
        '& div': {
            float: 'right',
        },
    },
    '&:last-child': {
        paddingLeft: '10px',
    },
})

const StyledLink = styled(Link)({
    color: '#0057A0',
    fontSize: '14px',
    textAlign: 'left',
    textDecoration: 'none',
})

const StyledAvatar = styled(Avatar)({
    width: '54px',
    height: '54px',
    boxShadow: '0 5px 8px 0 rgb(211 211 211)',
    border: 'solid 1px #ebebed',
    overflow: 'hidden',
    '& img': {
        boxShadow: '0 5px 8px 0 rgb(211 211 211)',
    },
})

const StyledCardHeader = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    padding: '10px 16px',
    borderBottom: '1px solid ' + colors.border,
})

const StyledAvatarContainer = styled(Box)({
    width: '66px',
    height: '66px',
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    backdropFilter: 'blur(3px)',
    top: '12px',
})

const StyledCompanyDetails = styled(Box)({
    width: '50%',
    '& p': {
        fontSize: '14px',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        width: '95%',
    },
})

const StyledBottomContent = styled(CardContent)({
    padding: '10px 15px',
    display: 'flex',
})

const StyledPersonalInfo = styled(Box)({
    width: '100%',
    '& p': {
        fontSize: '14px',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        width: '95%',
    },
})

function PeopleCard({ history, data }) {
    const { id, firstName, lastName, company, email, pathToImage, login } = data
    const { t } = useTranslation()

    const {
        location: { pathname },
    } = history

    return (
        <MyCard data-ai-explain="Identity card">
            <StyledCardHeader>
                <StyledAvatarContainer
                    style={{ position: 'relative', marginRight: '40px' }}
                >
                    <StyledAvatar
                        src={
                            pathToImage &&
                            MIDPathToImage(appConfig.BASE_EID_URL, pathToImage)
                        }
                        aria-label={`${firstName} ${lastName}`}
                    />
                </StyledAvatarContainer>
                <StyledFieldBlock>
                    <Box>
                        <Typography
                            style={{
                                textTransform: 'uppercase',
                                lineHeight: '1',
                                fontSize: '8px',
                            }}
                            variant="subtitle1"
                        >
                            {t('FirstName')}
                        </Typography>
                        <Typography variant="body2">
                            <StyledLink
                                component="button"
                                onClick={() =>
                                    history.push(
                                        `${pathname}/${id}?cardView=true`,
                                    )
                                }
                                data-ai-explain={`Open profile: ${firstName}`}
                            >
                                <TextFormatter
                                    value={firstName}
                                    maxTextWidth={110}
                                />
                            </StyledLink>
                        </Typography>
                    </Box>
                </StyledFieldBlock>
                <StyledFieldBlock>
                    <Box>
                        <Typography
                            style={{
                                textTransform: 'uppercase',
                                lineHeight: '1',
                                fontSize: '8px',
                            }}
                            variant="subtitle1"
                        >
                            {t('LastName')}
                        </Typography>
                        <StyledTypography variant="body2">
                            <TextFormatter
                                value={lastName}
                                maxTextWidth={110}
                            />
                        </StyledTypography>
                    </Box>
                </StyledFieldBlock>
            </StyledCardHeader>

            <StyledCardContent>
                <StyledCompanyDetails>
                    <Box>
                        <Typography
                            style={{
                                textTransform: 'uppercase',
                                lineHeight: '1',
                                fontSize: '8px',
                            }}
                            variant="subtitle1"
                        >
                            {t('CompanyName')}
                        </Typography>
                        <Typography variant="body2">
                            {company ? company : '-'}
                        </Typography>
                    </Box>
                </StyledCompanyDetails>
                {/* <StyledCompanyDetails >
                    <Box>
                        <Typography
                            style={{
                                textTransform: 'uppercase',
                            }}
                            variant="subtitle1"
                        >
                            Company ID
                        </Typography>
                        <Typography variant="body2">
                            {companyId ? companyId : '-'}
                        </Typography>
                    </Box>
                </StyledCompanyDetails> */}
                <StyledCompanyDetails>
                    <Box>
                        <Typography
                            style={{
                                textTransform: 'uppercase',
                                lineHeight: '1',
                                fontSize: '8px',
                            }}
                            variant="subtitle1"
                        >
                            {t('Login')}
                        </Typography>
                        <Tooltip title={login}>
                            <Typography variant="body2">
                                {login ? login : '-'}
                            </Typography>
                        </Tooltip>
                    </Box>
                </StyledCompanyDetails>
            </StyledCardContent>

            <StyledBottomContent>
                <StyledPersonalInfo>
                    <Box>
                        <Typography
                            style={{
                                textTransform: 'uppercase',
                                lineHeight: '1',
                                fontSize: '8px',
                            }}
                            variant="subtitle1"
                        >
                            {t('Email')}
                        </Typography>
                        <Tooltip title={email}>
                            <Typography variant="body2">
                                {email ? (
                                    <StyledLink
                                        href={`mailto:${email}`}
                                        style={{
                                            textTransform: 'none',
                                        }}
                                        data-ai-explain={`Send email to ${email}`}
                                    >
                                        {email}
                                    </StyledLink>
                                ) : (
                                    '-'
                                )}
                            </Typography>
                        </Tooltip>
                    </Box>
                </StyledPersonalInfo>
                {/* <Box>
                    <Box>
                        <Typography
                            style={{
                                textTransform: 'uppercase',
                            }}
                            variant="subtitle1"
                        >
                            Main Professional Id
                        </Typography>
                        <Typography variant="body2">No</Typography>
                    </Box>
                </Box> */}
            </StyledBottomContent>

            <StyledCardActions disableSpacing>
                <div
                    style={{
                        textAlign: 'center',
                        width: '100%',
                        padding: '5px 5px',
                    }}
                    data-ai-explain="View profile button"
                >
                    <Button.ViewProfile
                        onClick={() =>
                            history.push(`${pathname}/${id}?cardView=true`)
                        }
                    >
                        {t('ViewProfile')}
                    </Button.ViewProfile>
                </div>
            </StyledCardActions>
        </MyCard>
    )
}

PeopleCard.propTypes = {
    data: PropTypes.object.isRequired,
}

export default withRouter(PeopleCard)
